"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Simplify } from "@/prismicio-types";
import { Content } from "@prismicio/client";
import { PrismicNextImage, PrismicNextLink } from "@prismicio/next";
import { PrismicRichText } from "@prismicio/react";
import React from "react";
import employabilityAndCareerServiceSectionLeftShape from "@/assets/employabilityAndCareerServiceSectionLeftShape.svg";
import employabilityAndCareerServiceSectionRightShape from "@/assets/employabilityAndCareerServiceSectionRightShape.svg";
import Image from "next/image";
import { motion } from "framer-motion";
import noise from "@/assets/noise.png";

type Props = {
  data: Simplify<Content.CallToActionCardSliceGetInTouchCtaPrimary>;
};

export default function GetInTouchCta({ data }: Props) {
  return (
    <Card className="shadow-none border-none bg-[#033D61] text-white relative rounded-xl overflow-hidden">
      <div
        className="absolute inset-0 h-full w-full mix-blend-overlay opacity-[0.4]"
        style={{
          background: `url(${noise.src}) lightgray 0% 0% / 60.90909242630005px 60.90909242630005px repeat`,
        }}
      />
      <CardHeader className="relative z-20">
        <CardTitle className="flex items-center gap-4 text-3xl font-semibold">
          {/* icon */}

          <Button
            variant="secondary"
            size="icon"
            className="rounded-full bg-[#0798F2]/50"
          >
            <PrismicNextImage field={data.icon} alt="" />
          </Button>

          {/* heading */}

          <motion.div
            initial={{ y: 30, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.4, ease: "easeInOut" }}
            viewport={{ once: true }}
          >
            <PrismicRichText field={data.heading} />
          </motion.div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-10 xl:space-y-16 relative z-20">
        {/* subheading */}

        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeInOut", delay: 0.4 }}
          viewport={{ once: true }}
        >
          <PrismicRichText
            field={data.sub_heading}
            components={{
              paragraph: ({ children }) => (
                <p className="font-merriweather font-normal text-lg leading-7">
                  {children}
                </p>
              ),
            }}
          />
        </motion.div>

        {/* buttons */}
        <motion.div
          className="flex flex-col md:flex-row gap-2"
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.4, ease: "easeInOut", delay: 0.8 }}
          viewport={{ once: true }}
        >
          <Button
            asChild
            size="lg"
            className="rounded-full py-3 px-5 bg-linear-to-r from-[#43D9BD] to-[#0798F2] text-primary w-56"
          >
            <PrismicNextLink field={data.cta_button_primary} />
          </Button>

          <Button
            asChild
            size="lg"
            className="bg-transparent border rounded-full py-3 px-5 w-56"
          >
            <PrismicNextLink field={data.cta_button_secondary} />
          </Button>
        </motion.div>
      </CardContent>

      {/* overlays */}

      <Image
        src={employabilityAndCareerServiceSectionLeftShape.src}
        alt="left shape"
        width={521}
        height={482}
        className="absolute -left-20 -top-20 h-96 w-auto md:h-[480px] select-none"
      />
      <Image
        src={employabilityAndCareerServiceSectionRightShape.src}
        alt="right shape"
        width={521}
        height={482}
        className="absolute -right-20 -bottom-40 h-96 lg:h-[520px] w-auto select-none"
      />
    </Card>
  );
}
